import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { useCapacity, useWorkspace, useWorkspaceLogs } from "../api/queries";
import type { Capacity, Workspace } from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { WaitingList } from "../components/capacity/WaitingSection";
import { PageHeader } from "../components/layout/PageHeader";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { ConnectionBlock } from "../components/workspace/ConnectionBlock";
import { EventTimeline } from "../components/workspace/EventTimeline";
import { MountsList } from "../components/workspace/MountsList";
import { Utilization } from "../components/workspace/Utilization";
import { WorkspaceActions } from "../components/workspace/WorkspaceActions";
import { gpuCountLabel, workloadFamilyLabel } from "../lib/capacity";
import {
  formatDateTime,
  formatElapsed,
  formatRemaining,
  pluralize,
} from "../lib/format";
import { rowsFromWaiting, waitingNote } from "../lib/waiting";
import {
  isIdleAtRisk,
  isTerminalWorkspaceState,
  workspaceHeadline,
  workspaceStateLabel,
  workspaceTone,
} from "../lib/workload";

/**
 * The live line under the headline while a workspace has no machine yet.
 *
 * Seven minutes of an unchanging screen reads as "it is stuck". This says how
 * long the machine has been coming and how long it usually takes — the same
 * two numbers the capacity screen shows for the same machine.
 *
 * When the server published a `waiting` object it is the only source: the
 * machine it names is the one whose arrival serves this request's turn, which
 * may be somebody else's, and the free count is the one admission decides on.
 * The rest is the fallback for a bundle newer than the API it is talking to,
 * and it is why this function still takes the capacity payload.
 *
 * Args:
 *     workspace: The workspace being watched.
 *     capacity: The measured capacity, when it could be read.
 *
 * Returns:
 *     One sentence, or null when there is nothing true to add.
 */
function waitingProgress(
  workspace: Workspace,
  capacity: Capacity | undefined,
): string | null {
  if (workspace.state !== "waiting_for_node") {
    return null;
  }
  if (workspace.waiting) {
    return waitingNote(workspace.waiting, "GPU");
  }
  const eta = capacity?.node_start_eta_minutes ?? null;
  if (workspace.machine_requested_at !== null) {
    const since = formatElapsed(workspace.machine_requested_at);
    return eta === null
      ? `Machine requested ${since} ago.`
      : `Machine requested ${since} ago — they are usually ready in about ${eta} min.`;
  }
  const free = capacity?.gpu_free;
  if (free !== undefined) {
    return `${pluralize(free, "GPU")} free on the machines that are running.`;
  }
  return null;
}

/**
 * One workspace: what it is doing, how to get in, and how to keep it.
 *
 * Returns:
 *     The workspace screen.
 */
export function WorkspacePage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspace = useWorkspace(id);
  const { me } = useSession();
  const [logsOpen, setLogsOpen] = useState(false);

  const data = workspace.data;
  // The shared capacity query — the same cache the home screen fills. It is
  // read on every workspace, not only a queued one: on a fleet with more than
  // one kind of GPU it is what says which kind this workspace holds, and that
  // sentence must not depend on which screen the researcher came from.
  const capacity = useCapacity();
  // A workspace that never reached a machine has no log. Opening a panel that
  // can only say "there is nothing to read" is worse than not offering it.
  const hasLogs = data?.has_logs ?? false;
  const showLogs = hasLogs && (data?.state === "failed" || logsOpen);
  const logs = useWorkspaceLogs(id, showLogs);

  const header = (
    <PageHeader
      eyebrow="Workspace"
      title="Your workspace"
      back={{ to: "/", label: "GPUs" }}
      subtitle={data ? `${data.image}` : undefined}
      actions={
        data ? (
          <WorkspaceActions workspace={data} onStopped={() => navigate("/")} />
        ) : null
      }
    />
  );

  if (workspace.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Loading this workspace…" />
      </>
    );
  }

  if (workspace.isError) {
    const error = workspace.error;
    if (isClusterProblem(error)) {
      return (
        <>
          {header}
          <ClusterProblem error={error} onRetry={() => void workspace.refetch()} />
        </>
      );
    }
    if (error instanceof ApiError && (error.isNotFound || error.isForbidden)) {
      return (
        <>
          {header}
          <p className="state-block">
            This workspace does not exist any more, or it belongs to somebody
            else.
          </p>
        </>
      );
    }
    return (
      <>
        {header}
        <ErrorBlock error={error} onRetry={() => void workspace.refetch()} />
      </>
    );
  }

  const detail = workspace.data;
  const tone = workspaceTone(detail);
  const progress = waitingProgress(detail, capacity.data);

  return (
    <>
      {header}

      <section className="state-line" data-tone={tone}>
        <span className="badge" data-tone={tone}>
          <span className="badge__dot" aria-hidden="true" />
          {workspaceStateLabel(detail)}
        </span>
        <p className="state-line__text">{workspaceHeadline(detail)}</p>
        {progress ? <p className="state-line__note">{progress}</p> : null}
      </section>

      {/* The line, only when somebody is really in front: at the head the
          sentence above already says so, and a list of one row saying "you"
          would be furniture. */}
      {detail.waiting && detail.waiting.ahead.length > 0 ? (
        <WaitingList
          rows={rowsFromWaiting(detail.waiting, {
            id: detail.id,
            user: detail.user,
            gpus: detail.gpus,
            cores: null,
          })}
          unit="GPU"
          you={me.username}
        />
      ) : null}

      <div className="split">
        <div className="split__main">
          <h2 className="section__title">What has happened</h2>
          <EventTimeline events={detail.events} />

          {showLogs ? (
            <div className="logs">
              <h3 className="section__title">
                {detail.started_at ? "Log" : "Startup log"}
              </h3>
              {logs.isPending ? (
                <LoadingBlock label="Reading the log…" />
              ) : logs.isError ? (
                <ErrorBlock error={logs.error} onRetry={() => void logs.refetch()} />
              ) : (
                <pre className="log">{logs.data || "The log is empty."}</pre>
              )}
            </div>
          ) : hasLogs ? (
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setLogsOpen(true)}
            >
              {detail.started_at ? "Show the log" : "Show the startup log"}
            </button>
          ) : (
            <p className="aside__note">
              {/* The state is what says a workspace has ended — never
                  `started_at`, which a resize and a re-placement both leave
                  behind them. Reading the field instead told a workspace
                  queued for a bigger machine that its log had gone back "when
                  this workspace ended", directly under a trace line saying it
                  was being opened again. */}
              {isTerminalWorkspaceState(detail.state)
                ? detail.node
                  ? "The log went back with the machine when this workspace ended."
                  : "There is no log to read: this workspace never reached a machine."
                : detail.started_at
                  ? "The log will be here again as soon as the workspace is back on a machine."
                  : "There is no log yet: this workspace has not reached a machine."}
            </p>
          )}
        </div>

        <aside className="split__aside">
          <h2 className="section__title">Connect</h2>
          {detail.connection?.ready ? (
            <ConnectionBlock connection={detail.connection} />
          ) : (
            <p className="aside__note">
              The connection details appear here as soon as the workspace is
              ready and its port is open.
            </p>
          )}

          <h2 className="section__title aside__spaced">GPU activity</h2>
          <Utilization value={detail.utilization_pct} gpus={detail.gpus} />
          <p className="aside__meta">
            {gpuCountLabel(
              detail.gpus,
              workloadFamilyLabel(capacity.data, detail.gpu_family),
            )}
            {detail.cores > 0 ? ` · ${pluralize(detail.cores, "core")}` : ""}
            {detail.memory_gb > 0 ? ` · ${detail.memory_gb} GiB of memory` : ""}
            {detail.node ? (
              <>
                {" on "}
                <span className="mono">{detail.node}</span>
              </>
            ) : null}
            {detail.gpu_indices.length > 0
              ? ` (${detail.gpu_indices.map((index) => `#${index}`).join(", ")})`
              : ""}
          </p>
          {/* A limit is worth explaining while it can still be met. On a
              workspace that has ended, "starts again on the same machine"
              would be a promise about a machine it no longer has. */}
          {detail.memory_gb > 0 && !isTerminalWorkspaceState(detail.state) ? (
            <p className="aside__note">
              That memory is a limit as well as a reservation: a program that
              goes past it is stopped, everything else in the workspace stops
              with it, and the workspace starts again on the same machine.{" "}
              <Link to="/docs/memory">What happens then</Link>
            </p>
          ) : null}

          <h2 className="section__title aside__spaced">Time</h2>
          <p className="aside__meta">
            {isTerminalWorkspaceState(detail.state) ? (
              "This workspace is not running any more. Your files in /home are still there."
            ) : detail.expires_at ? (
              <>
                Stops in {formatRemaining(detail.expires_at)}, at{" "}
                {formatDateTime(detail.expires_at)}.
              </>
            ) : (
              "The clock starts when the workspace becomes usable."
            )}
          </p>
          {isIdleAtRisk(detail) && detail.idle_stops_at ? (
            <p className="aside__meta" data-tone="warning">
              Unused since {formatDateTime(detail.idle_since)}: it stops in{" "}
              {formatRemaining(detail.idle_stops_at)} unless the GPUs are used
              or you add time.
            </p>
          ) : null}

          <h2 className="section__title aside__spaced">Your folders</h2>
          <MountsList mounts={detail.mounts} />
        </aside>
      </div>
    </>
  );
}
